import type { ComponentPropsWithoutRef } from "react";
import type { Control } from "react-hook-form";
import { t } from "ttag";

import type { CreateSession } from "@/features/coaching/models";
import { SESSION_FREQUENCY } from "@/features/coaching/models";
import { Form, Select } from "@/shared/ui";

type FieldValue = Pick<CreateSession, "frequency">;

type SessionFrequencyControl<T> = T extends FieldValue ? Control<T> : never;

type SessionFrequencyFieldProps<T extends FieldValue> =
  ComponentPropsWithoutRef<typeof Form.Item> & {
    control: SessionFrequencyControl<T>;
    onValueChange: (value: T["frequency"]) => void;
  };

export const SessionFrequencyField = <T extends FieldValue>({
  control,
  onValueChange,
  ...props
}: SessionFrequencyFieldProps<T>) => {
  const OPTIONS = [
    { value: SESSION_FREQUENCY.ONE_TIME, label: t`One-time` },
    { value: SESSION_FREQUENCY.WEEKLY, label: t`Weekly` },
    { value: SESSION_FREQUENCY.BI_WEEKLY, label: t`Bi-weekly` },
  ] as const;

  return (
    <Form.Field
      control={control}
      name="frequency"
      render={({ field: { onChange, ...field } }) => (
        <Form.Item {...props}>
          <Form.Label aria-required>{t`Frequency`}</Form.Label>
          <Form.Control>
            <Select
              options={OPTIONS}
              onChange={(value) => {
                onChange(value);
                onValueChange(value);
              }}
              {...field}
            />
          </Form.Control>
          <Form.Message compact={false} />
        </Form.Item>
      )}
    />
  );
};
